import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from '../lib/axios';
import { 
  Users, 
  Baby, 
  AlertTriangle, 
  TrendingUp,
  Clock,
  FileWarning,
  Bell,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    incidents_today: 0,
    send_home_today: 0,
    total_children: 0,
    total_classrooms: 0,
  });
  const [recentIncidents, setRecentIncidents] = useState([]);
  const [ratioAlerts, setRatioAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, incidentsRes] = await Promise.all([
        axios.get('/dashboard/stats'),
        axios.get('/incident-reports'),
      ]);
      
      setStats(statsRes.data);
      setRecentIncidents(incidentsRes.data.slice(0, 5));

      // Fetch ratio alerts for directors
      if (user?.role === 'admin') {
        try {
          const alertsRes = await axios.get('/ratio-alerts');
          setRatioAlerts(alertsRes.data);
        } catch (e) {
          console.log('Could not fetch ratio alerts');
        }
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const acknowledgeAlert = async (alertId) => {
    try {
      await axios.post(`/ratio-alerts/${alertId}/acknowledge`);
      setRatioAlerts(prev => prev.filter(a => a.id !== alertId));
      toast.success('Alert acknowledged');
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };

  const statCards = [
    {
      title: 'Total Children',
      value: stats.total_children,
      icon: Baby,
      color: 'bg-blue-50 text-blue-600',
      border: 'border-blue-100',
    },
    {
      title: 'Classrooms',
      value: stats.total_classrooms,
      icon: Users,
      color: 'bg-purple-50 text-purple-600',
      border: 'border-purple-100',
    },
    {
      title: 'Incidents Today',
      value: stats.incidents_today,
      icon: AlertTriangle,
      color: 'bg-rose-50 text-rose-600',
      border: 'border-rose-100',
    },
    {
      title: 'Send Home Today',
      value: stats.send_home_today,
      icon: FileWarning,
      color: 'bg-amber-50 text-amber-600',
      border: 'border-amber-100',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Welcome back, {user?.name}!
        </h1>
        <p className="text-slate-600">Here&apos;s what&apos;s happening today in your center</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.title}
              className={`bg-white rounded-xl border ${stat.border} p-6 shadow-sm hover:shadow-md transition-shadow`}
              data-testid={`stat-card-${stat.title.toLowerCase().replace(/ /g, '-')}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">{stat.title}</p>
                  <p className="text-3xl font-bold text-slate-900 mt-2">{stat.value}</p>
                </div>
                <div className={`w-12 h-12 rounded-full ${stat.color} flex items-center justify-center`}>
                  <Icon className="w-6 h-6" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Ratio Alerts */}
      {ratioAlerts.length > 0 && (
        <div className="bg-red-50 border-2 border-red-200 rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <Bell className="w-5 h-5 text-red-600 animate-pulse" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-red-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                ⚠️ Ratio Alerts
              </h2>
              <p className="text-sm text-red-700">Immediate action required for Texas licensing compliance</p>
            </div>
          </div>
          <div className="space-y-3">
            {ratioAlerts.map((alert) => (
              <div key={alert.id} className="bg-white border border-red-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-bold text-slate-900">{alert.classroom_name}</h3>
                    <p className="text-sm text-slate-700 mt-1">{alert.message}</p>
                    <div className="flex gap-4 mt-2 text-xs text-slate-500">
                      <span>Children: {alert.present_children}</span>
                      <span>Staff: {alert.present_staff}</span>
                      <span>Required: {alert.required_staff}</span>
                      <span>Ratio: {alert.ratio_limit}</span>
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => acknowledgeAlert(alert.id)}
                    className="shrink-0"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Acknowledge
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Incidents */}
      {recentIncidents.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Recent Incidents
            </h2>
            <Link 
              to="/incidents" 
              className="text-sm text-primary hover:text-primary/80 font-medium"
              data-testid="view-all-incidents-link"
            >
              View All
            </Link>
          </div>
          <div className="space-y-3">
            {recentIncidents.map((incident) => (
              <div
                key={incident.id}
                className="p-4 bg-slate-50 rounded-lg border border-slate-100 hover:border-primary/20 transition-colors"
                data-testid={`incident-item-${incident.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        incident.incident_type === 'injury' ? 'bg-rose-100 text-rose-800' :
                        incident.incident_type === 'illness' ? 'bg-amber-100 text-amber-800' :
                        'bg-slate-100 text-slate-800'
                      }`}>
                        {incident.incident_type}
                      </span>
                      <span className="text-sm text-slate-600 timestamp">
                        {new Date(incident.datetime).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-sm text-slate-900 mt-2 line-clamp-2">{incident.description}</p>
                  </div>
                  <AlertTriangle className="w-5 h-5 text-slate-400 ml-3 flex-shrink-0" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link
          to="/classrooms"
          className="bg-white rounded-xl border border-slate-100 p-6 hover:border-primary/20 hover:shadow-md transition-all group"
          data-testid="quick-action-classrooms"
        >
          <Users className="w-8 h-8 text-primary mb-3 group-hover:scale-110 transition-transform" />
          <h3 className="font-semibold text-slate-900 mb-1">Manage Classrooms</h3>
          <p className="text-sm text-slate-600">View and organize your classrooms</p>
        </Link>

        <Link
          to="/children"
          className="bg-white rounded-xl border border-slate-100 p-6 hover:border-primary/20 hover:shadow-md transition-all group"
          data-testid="quick-action-children"
        >
          <Baby className="w-8 h-8 text-primary mb-3 group-hover:scale-110 transition-transform" />
          <h3 className="font-semibold text-slate-900 mb-1">Children Records</h3>
          <p className="text-sm text-slate-600">Access child profiles and logs</p>
        </Link>

        <Link
          to="/reports"
          className="bg-white rounded-xl border border-slate-100 p-6 hover:border-primary/20 hover:shadow-md transition-all group"
          data-testid="quick-action-reports"
        >
          <TrendingUp className="w-8 h-8 text-primary mb-3 group-hover:scale-110 transition-transform" />
          <h3 className="font-semibold text-slate-900 mb-1">Generate Reports</h3>
          <p className="text-sm text-slate-600">Export compliance documents</p>
        </Link>
      </div>

      {stats.incidents_today > 5 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-amber-900">High Incident Activity</h4>
              <p className="text-sm text-amber-800 mt-1">
                There have been {stats.incidents_today} incidents reported today. Please review and ensure all reports are finalized.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
